import {
  AppState,
  Connector,
  Wire,
  HarnessNode,
  ConnectorType,
  Pin,
  Segment,
  InteractionState,
  HarnessBackground
} from '../types';
import { WIRE_DEFAULTS } from '../utils/constants';
import { connectorCenter, GeometryUtils, Point } from '../utils/geometry';
import { nextPinLabel } from '../utils/pins';
import { HistoryManager, HistoryState } from './HistoryManager';

/** Canonical id for the unordered vertex pair of a harness section. */
export function segmentId(aId: string, bId: string): string {
  return [aId, bId].sort().join('|');
}

export class StateManager {
  private state: AppState;
  private interactionState: InteractionState;
  private listeners: Array<(state: AppState) => void> = [];
  private interactionListeners: Array<(state: InteractionState) => void> = [];
  private history = new HistoryManager<AppState>();

  constructor() {
    this.state = {
      connectors: [],
      wires: [],
      nodes: [],
      connectorTypes: [],
      segments: []
    };
    this.interactionState = {
      mode: 'normal'
    };
  }

  getState(): AppState {
    return { ...this.state };
  }

  setState(newState: Partial<AppState>): void {
    this.record();
    this.state = { ...this.state, ...newState };
    this.notifyListeners();
  }

  // Undo/redo history

  /** Opens an undo batch so a continuous gesture becomes a single undo step. */
  beginBatch(): void {
    this.history.beginBatch();
  }

  /** Closes the current undo batch. */
  endBatch(): void {
    this.history.endBatch();
  }

  undo(): void {
    const previous = this.history.undo(this.snapshot());
    if (previous) {
      this.state = previous;
      this.notifyListeners();
    }
  }

  redo(): void {
    const next = this.history.redo(this.snapshot());
    if (next) {
      this.state = next;
      this.notifyListeners();
    }
  }

  getHistoryState(): HistoryState {
    return this.history.getState();
  }

  subscribeToHistory(listener: (state: HistoryState) => void): () => void {
    return this.history.subscribe(listener);
  }

  /**
   * Deep-clones the state for history, but shares the (immutable) background by
   * reference. Deep-cloning it would duplicate the embedded image data URL across
   * every snapshot; sharing the reference keeps only the distinct background
   * versions in memory while still restoring the right one on undo.
   */
  private snapshot(): AppState {
    const { background, ...rest } = this.state;
    const clone: AppState = JSON.parse(JSON.stringify(rest));
    if (background) {
      clone.background = background;
    }
    return clone;
  }

  /** Records the pre-mutation state so the change can be undone. */
  private record(): void {
    this.history.record(this.snapshot());
  }

  /**
   * Replaces the entire state from a loaded/imported harness: applies wire
   * electrical defaults and rebuilds the segment set (preserving any lengths
   * present in the loaded data).
   */
  loadState(state: AppState): void {
    this.state = {
      connectors: state.connectors ?? [],
      wires: (state.wires ?? []).map(w => this.withWireDefaults(w)),
      nodes: state.nodes ?? [],
      connectorTypes: state.connectorTypes ?? [],
      segments: state.segments ?? [],
      background: state.background
    };
    this.recomputeSegments();
    this.history.clear();
    this.setSelection([]);
    this.notifyListeners();
  }

  addConnector(connector: Connector): void {
    this.record();
    this.state.connectors.push(connector);
    this.notifyListeners();
  }

  removeConnector(connectorId: string): void {
    this.record();
    const removedWires = this.state.wires.filter(
      w => w.startConnectorId === connectorId || w.endConnectorId === connectorId
    );
    const affectedNodeIds = removedWires.flatMap(w => w.path);

    this.state.connectors = this.state.connectors.filter(t => t.id !== connectorId);
    this.state.wires = this.state.wires.filter(
      w => w.startConnectorId !== connectorId && w.endConnectorId !== connectorId
    );
    this.removeOrphanedNodes(affectedNodeIds);
    this.recomputeSegments();
    this.notifyListeners();
  }

  updateConnector(connectorId: string, updates: Partial<Connector>): void {
    const index = this.state.connectors.findIndex(t => t.id === connectorId);
    if (index !== -1) {
      this.record();
      this.state.connectors[index] = { ...this.state.connectors[index], ...updates };
      this.notifyListeners();
    }
  }

  addWire(wire: Wire): void {
    this.record();
    this.state.wires.push(this.withWireDefaults(wire));
    this.recomputeSegments();
    this.notifyListeners();
  }

  removeWire(wireId: string): void {
    this.record();
    const removed = this.state.wires.find(w => w.id === wireId);
    this.state.wires = this.state.wires.filter(w => w.id !== wireId);
    this.removeOrphanedNodes(removed?.path ?? []);
    this.recomputeSegments();
    this.notifyListeners();
  }

  /**
   * Removes any of the given nodes that no wire routes through any more, so
   * deleting a wire does not leave dangling waypoints on the canvas. Nodes not
   * in the candidate set (e.g. unrelated waypoints) are left untouched.
   */
  private removeOrphanedNodes(candidateNodeIds: string[]): void {
    if (candidateNodeIds.length === 0) {
      return;
    }
    const stillUsed = new Set(this.state.wires.flatMap(w => w.path));
    this.state.nodes = this.state.nodes.filter(
      n => !candidateNodeIds.includes(n.id) || stillUsed.has(n.id)
    );
  }

  updateWire(wireId: string, updates: Partial<Wire>): void {
    const index = this.state.wires.findIndex(w => w.id === wireId);
    if (index !== -1) {
      this.record();
      this.state.wires[index] = { ...this.state.wires[index], ...updates };
      this.recomputeSegments();
      this.notifyListeners();
    }
  }

  /** Fills in wire electrical defaults for any field the caller left undefined. */
  private withWireDefaults(wire: Wire): Wire {
    return {
      ...wire,
      color: wire.color ?? WIRE_DEFAULTS.COLOR,
      gauge: wire.gauge ?? WIRE_DEFAULTS.GAUGE,
      wireType: wire.wireType ?? WIRE_DEFAULTS.TYPE
    };
  }

  addNode(node: HarnessNode): void {
    this.record();
    this.state.nodes.push(node);
    this.notifyListeners();
  }

  removeNode(nodeId: string): void {
    this.record();
    this.state.nodes = this.state.nodes.filter(n => n.id !== nodeId);
    // Remove node from all wire paths
    this.state.wires.forEach(wire => {
      wire.path = wire.path.filter(pathNodeId => pathNodeId !== nodeId);
    });
    this.recomputeSegments();
    this.notifyListeners();
  }

  updateNode(nodeId: string, updates: Partial<HarnessNode>): void {
    const index = this.state.nodes.findIndex(n => n.id === nodeId);
    if (index !== -1) {
      this.record();
      this.state.nodes[index] = { ...this.state.nodes[index], ...updates };
      this.notifyListeners();
    }
  }

  // Background reference image

  /** Sets (or replaces) the reference image placed behind the diagram. */
  setBackground(background: HarnessBackground): void {
    this.record();
    this.state = { ...this.state, background };
    this.notifyListeners();
  }

  /** Updates fields of the current background; a no-op when none is set. */
  updateBackground(updates: Partial<HarnessBackground>): void {
    if (!this.state.background) {
      return;
    }
    this.record();
    this.state = { ...this.state, background: { ...this.state.background, ...updates } };
    this.notifyListeners();
  }

  /** Removes the reference image; a no-op when none is set. */
  removeBackground(): void {
    if (!this.state.background) {
      return;
    }
    this.record();
    const { background, ...rest } = this.state;
    this.state = { ...rest };
    this.notifyListeners();
  }

  subscribe(listener: (state: AppState) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
  }

  // Interaction State Management
  getInteractionState(): InteractionState {
    return { ...this.interactionState };
  }

  setInteractionMode(mode: InteractionState['mode']): void {
    this.interactionState = { ...this.interactionState, mode };
    this.notifyInteractionListeners();
  }

  setSelectedNode(nodeId?: string): void {
    this.interactionState = { ...this.interactionState, selectedNodeId: nodeId };
    this.notifyInteractionListeners();
  }

  /**
   * Replaces the set of selected connectors and nodes. This is view state only:
   * it is not recorded in history and is not persisted. No-op changes are
   * ignored so frequent updates do not trigger needless redraws.
   */
  setSelection(ids: string[]): void {
    const current = this.interactionState.selectedIds ?? [];
    if (current.length === ids.length && current.every(id => ids.includes(id))) {
      return;
    }
    this.interactionState = { ...this.interactionState, selectedIds: [...ids] };
    this.notifyInteractionListeners();
  }

  getSelection(): string[] {
    return [...(this.interactionState.selectedIds ?? [])];
  }

  /**
   * Removes the given connectors and nodes as one undo step and clears the
   * selection. Ids that are neither a connector nor a node are ignored.
   */
  deleteElements(ids: string[]): void {
    if (ids.length === 0) {
      return;
    }
    this.beginBatch();
    ids.forEach(id => {
      if (this.state.connectors.some(c => c.id === id)) {
        this.removeConnector(id);
      } else if (this.state.nodes.some(n => n.id === id)) {
        this.removeNode(id);
      }
    });
    this.endBatch();
    this.setSelection([]);
  }

  /**
   * Marks the wire whose route should be highlighted on the canvas (or clears it
   * when omitted). This is view state only: it is not recorded in history and is
   * not persisted.
   */
  setHighlightedWire(wireId?: string): void {
    if (this.interactionState.highlightedWireId === wireId) {
      return;
    }
    this.interactionState = { ...this.interactionState, highlightedWireId: wireId };
    this.notifyInteractionListeners();
  }

  setPreviewWire(previewWire?: InteractionState['previewWire']): void {
    this.interactionState = { ...this.interactionState, previewWire };
    this.notifyInteractionListeners();
  }

  setDragData(dragData?: InteractionState['dragData']): void {
    this.interactionState = { ...this.interactionState, dragData };
    this.notifyInteractionListeners();
  }

  subscribeToInteractions(listener: (state: InteractionState) => void): () => void {
    this.interactionListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.interactionListeners = this.interactionListeners.filter(l => l !== listener);
    };
  }

  private notifyInteractionListeners(): void {
    this.interactionListeners.forEach(listener => listener(this.interactionState));
  }

  // Wire management utilities
  getWiresByConnector(connectorId: string): Wire[] {
    return this.state.wires.filter(
      wire => wire.startConnectorId === connectorId || wire.endConnectorId === connectorId
    );
  }

  getWiresByNode(nodeId: string): Wire[] {
    return this.state.wires.filter(wire => wire.path.includes(nodeId));
  }

  insertNodeInWirePath(wireId: string, nodeId: string, insertIndex?: number): void {
    const wire = this.state.wires.find(w => w.id === wireId);
    if (!wire) return;

    const newPath = [...wire.path];
    if (insertIndex !== undefined) {
      newPath.splice(insertIndex, 0, nodeId);
    } else {
      newPath.push(nodeId);
    }

    this.updateWire(wireId, { path: newPath });
  }

  generateUniqueId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  generateWireName(): string {
    const existingWires = this.state.wires.length;
    return `Wire ${existingWires + 1}`;
  }

  // Connector type (catalog) management

  addConnectorType(connectorType: ConnectorType): void {
    this.record();
    this.state.connectorTypes.push(connectorType);
    this.notifyListeners();
  }

  updateConnectorType(typeId: string, updates: Partial<ConnectorType>): void {
    const index = this.state.connectorTypes.findIndex(t => t.id === typeId);
    if (index !== -1) {
      this.record();
      this.state.connectorTypes[index] = { ...this.state.connectorTypes[index], ...updates };
      this.notifyListeners();
    }
  }

  removeConnectorType(typeId: string): void {
    this.record();
    this.state.connectorTypes = this.state.connectorTypes.filter(t => t.id !== typeId);
    this.notifyListeners();
  }

  // Pin management

  /**
   * Replaces the pin instances on a connector and clears any wire pin
   * assignment on that connector that no longer refers to an existing pin.
   */
  setConnectorPins(connectorId: string, pins: Pin[]): void {
    const index = this.state.connectors.findIndex(c => c.id === connectorId);
    if (index === -1) {
      return;
    }
    this.record();
    this.state.connectors[index] = { ...this.state.connectors[index], pins };

    const validPinIds = new Set(pins.map(p => p.id));
    this.state.wires = this.state.wires.map(wire => {
      let next = wire;
      if (wire.startConnectorId === connectorId && wire.startPinId && !validPinIds.has(wire.startPinId)) {
        next = { ...next, startPinId: undefined };
      }
      if (wire.endConnectorId === connectorId && wire.endPinId && !validPinIds.has(wire.endPinId)) {
        next = { ...next, endPinId: undefined };
      }
      return next;
    });

    this.notifyListeners();
  }

  /**
   * Adds a pin with an auto-generated label to a connector and returns its id,
   * used when a wire is started or finished on a fresh pin. Returns undefined if
   * the connector no longer exists.
   */
  addPin(connectorId: string): string | undefined {
    const connector = this.state.connectors.find(c => c.id === connectorId);
    if (!connector) {
      return undefined;
    }
    const pin: Pin = { id: this.generateUniqueId(), label: nextPinLabel(connector.pins ?? []) };
    this.setConnectorPins(connectorId, [...(connector.pins ?? []), pin]);
    return pin.id;
  }

  /** Assigns (or clears, when pinId is undefined) the pin a wire end terminates on. */
  assignWirePin(wireId: string, end: 'start' | 'end', pinId?: string): void {
    const key = end === 'start' ? 'startPinId' : 'endPinId';
    this.updateWire(wireId, { [key]: pinId });
  }

  /** All wires terminating on a given pin (supports multiple crimps per pin). */
  getWiresByPin(connectorId: string, pinId: string): Wire[] {
    return this.state.wires.filter(
      wire =>
        (wire.startConnectorId === connectorId && wire.startPinId === pinId) ||
        (wire.endConnectorId === connectorId && wire.endPinId === pinId)
    );
  }

  // Segment (per-section length) management

  /** Ordered vertex ids a wire passes through: start connector, path nodes, end connector. */
  getWireRoute(wire: Wire): string[] {
    return [wire.startConnectorId, ...wire.path, wire.endConnectorId];
  }

  /** Segments a wire traverses, in route order. */
  getWireSegments(wireId: string): Segment[] {
    const wire = this.state.wires.find(w => w.id === wireId);
    if (!wire) {
      return [];
    }
    const route = this.getWireRoute(wire);
    const segments: Segment[] = [];
    for (let i = 0; i < route.length - 1; i++) {
      const segment = this.state.segments.find(s => s.id === segmentId(route[i], route[i + 1]));
      if (segment) {
        segments.push(segment);
      }
    }
    return segments;
  }

  /** Total length of a wire, summed across the sections it passes through. */
  getWireLength(wireId: string): number {
    return this.getWireSegments(wireId).reduce((total, segment) => total + segment.length, 0);
  }

  setSegmentLength(aId: string, bId: string, length: number): void {
    const id = segmentId(aId, bId);
    const index = this.state.segments.findIndex(s => s.id === id);
    if (index !== -1) {
      this.record();
      this.state.segments[index] = { ...this.state.segments[index], length };
      this.notifyListeners();
    }
  }

  /** Sets the free-form notes on a section (e.g. "shell", "twisted pair"). */
  setSegmentNotes(aId: string, bId: string, notes: string): void {
    const id = segmentId(aId, bId);
    const index = this.state.segments.findIndex(s => s.id === id);
    if (index !== -1) {
      this.record();
      this.state.segments[index] = { ...this.state.segments[index], notes };
      this.notifyListeners();
    }
  }

  /** The section between two adjacent vertices, if one exists. */
  getSegment(aId: string, bId: string): Segment | undefined {
    return this.state.segments.find(s => s.id === segmentId(aId, bId));
  }

  /** World-space anchor of a vertex: a connector's centre or a node's position. */
  private vertexPosition(id: string): Point | undefined {
    const connector = this.state.connectors.find(c => c.id === id);
    if (connector) {
      return connectorCenter(connector);
    }
    const node = this.state.nodes.find(n => n.id === id);
    if (node) {
      return { x: node.xPosition, y: node.yPosition };
    }
    return undefined;
  }

  /**
   * Straight-line distance between the two vertices of a section, in world units.
   * With a calibrated background one world unit is one millimetre, so this is a
   * physical length estimate suggested from the layout. Returns null when either
   * vertex is missing.
   */
  suggestedSegmentLength(aId: string, bId: string): number | null {
    const a = this.vertexPosition(aId);
    const b = this.vertexPosition(bId);
    if (!a || !b) {
      return null;
    }
    return Math.round(GeometryUtils.distance(a, b));
  }

  /**
   * Sections that have no measured length yet but do have a usable layout
   * distance, paired with that suggested length.
   */
  private fillableSegments(): Array<{ segment: Segment; length: number }> {
    const result: Array<{ segment: Segment; length: number }> = [];
    this.state.segments.forEach(segment => {
      if (segment.length > 0) {
        return;
      }
      const suggested = this.suggestedSegmentLength(segment.aId, segment.bId);
      if (suggested !== null && suggested > 0) {
        result.push({ segment, length: suggested });
      }
    });
    return result;
  }

  /** How many unmeasured sections could be filled from the layout right now. */
  getFillableSegmentCount(): number {
    return this.fillableSegments().length;
  }

  /**
   * Fills in a length for every section that has none yet, using the straight-line
   * distance between its vertices (a millimetre estimate once the background is
   * calibrated). Applies as a single undo step and returns the number of sections
   * updated.
   */
  fillMissingSegmentLengthsFromLayout(): number {
    const targets = this.fillableSegments();
    if (targets.length === 0) {
      return 0;
    }
    this.beginBatch();
    targets.forEach(({ segment, length }) => this.setSegmentLength(segment.aId, segment.bId, length));
    this.endBatch();
    return targets.length;
  }

  /** Every wire routed through the section between two adjacent vertices. */
  getWiresInSegment(aId: string, bId: string): Wire[] {
    const id = segmentId(aId, bId);
    return this.state.wires.filter(wire => {
      const route = this.getWireRoute(wire);
      for (let i = 0; i < route.length - 1; i++) {
        if (segmentId(route[i], route[i + 1]) === id) {
          return true;
        }
      }
      return false;
    });
  }

  /**
   * Rebuilds the segment set from current wire routes: adds sections that appear
   * in a route (preserving any existing length) and drops sections no wire uses.
   */
  private recomputeSegments(): void {
    const previous = new Map(this.state.segments.map(s => [s.id, s]));
    const next = new Map<string, Segment>();

    this.state.wires.forEach(wire => {
      const route = this.getWireRoute(wire);
      for (let i = 0; i < route.length - 1; i++) {
        const aId = route[i];
        const bId = route[i + 1];
        if (aId === bId) {
          continue; // same-connector jumper: no physical section to measure
        }
        const id = segmentId(aId, bId);
        if (!next.has(id)) {
          next.set(id, previous.get(id) ?? { id, aId, bId, length: 0 });
        }
      }
    });

    this.state.segments = Array.from(next.values());
  }
}